import { useEffect, useRef } from "react";
import * as echarts from 'echarts';
import { QueryBoundsData } from '@/queries/api';

interface SensorData {
  name: string;
  location: string;
  min: number;
  p25: number;
  median: number;
  p75: number;
  max: number;
  installation_name: string | null;
  location_name: string | null;
  organization_name: string | null;
}

interface UseBoxPlotChartProps {
  sensorData: SensorData[];
  isLoading: boolean;
  bounds?: QueryBoundsData;
  displayName?: string;
  unit?: string;
}

export const useBoxPlotChart = ({ sensorData, isLoading, bounds, displayName, unit }: UseBoxPlotChartProps) => {
  const chartRef = useRef(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    // Initialize main boxplot chart
    if (!chartRef.current || isLoading || sensorData.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(chartRef.current);

    // Reverse data because ECharts renders y-axis categories from bottom to top
    const reversedData = [...sensorData].reverse();

    const boxplotData = reversedData.map((sensor) => [
      sensor.min,
      sensor.p25,
      sensor.median,
      sensor.p75,
      sensor.max,
    ]);

    const categoryLabels = reversedData.map((sensor) => `${sensor.name} (${sensor.organization_name} / ${sensor.location_name} / ${sensor.installation_name})`);

    const chartTitle = displayName && unit ? `${displayName} (${unit})` : 'Thermistor 7 (°C)';

    const option = {
      title: {
        text: chartTitle,
        left: 'center',
        textStyle: {
          fontSize: 24,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: function (param: any) {
          const sensor = reversedData[param.dataIndex];
          const headerLines = [];

          // Build contextual hierarchy if available
          if (sensor.organization_name) {
            headerLines.push(`<b>${sensor.organization_name}</b>`);
          }
          if (sensor.location_name) {
            headerLines.push(`${sensor.location_name}`);
          }
          if (sensor.installation_name) {
            headerLines.push(`${sensor.installation_name}`);
          }
          headerLines.push(`<b>Sensor Kit:</b> ${sensor.name}`);

          return [
            ...headerLines,
            '',
            `Max: ${sensor.max.toFixed(2)}`,
            `Q3: ${sensor.p75.toFixed(2)}`,
            `Median: ${sensor.median.toFixed(2)}`,
            `Q1: ${sensor.p25.toFixed(2)}`,
            `Min: ${sensor.min.toFixed(2)}`,
          ].join('<br/>');
        },
      },
      grid: {
        left: '15%',
        right: '10%',
        top: '15%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: chartTitle,
        nameLocation: 'middle',
        nameGap: 30,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e5e7eb',
            width: 1,
            type: 'solid',
          },
        },
        axisLine: {
          lineStyle: {
            color: '#6b7280',
          },
        },
      },
      yAxis: {
        type: 'category',
        data: categoryLabels,
        boundaryGap: true,
        splitLine: {
          show: true,
          alignWithLabel: true,
          lineStyle: {
            color: '#e5e7eb',
            width: 1,
            type: 'dashed',
          },
        },
        axisLine: {
          lineStyle: {
            color: '#6b7280',
          },
        },
        axisLabel: {
          interval: 0,
          fontSize: 10,
          color: '#374151',
        },
      },
      series: [
        {
          type: 'boxplot',
          data: boxplotData,
          itemStyle: {
            borderColor: '#3b82f6',
            borderWidth: 2,
            color: 'rgba(59, 130, 246, 0.4)',
          },
          emphasis: {
            itemStyle: {
              borderColor: '#1d4ed8',
              borderWidth: 3,
              color: 'rgba(59, 130, 246, 0.6)',
            },
          },
          markLine: {
            data: [
              ...(bounds?.min_bound !== null && bounds?.min_bound !== undefined ? [{
                xAxis: bounds.min_bound,
                label: {
                  formatter: `Lower Bound: ${bounds.min_bound} ${unit || '°C'}`,
                  position: 'start',
                },
                lineStyle: {
                  color: '#dc2626',
                  type: 'dashed',
                  width: 2,
                },
              }] : []),
              ...(bounds?.max_bound !== null && bounds?.max_bound !== undefined ? [{
                xAxis: bounds.max_bound,
                label: {
                  formatter: `Upper Bound: ${bounds.max_bound} ${unit || '°C'}`,
                  position: 'start',
                },
                lineStyle: {
                  color: '#dc2626',
                  type: 'dashed',
                  width: 2,
                },
              }] : []),
            ],
          },
        },
      ],
    };

    chartInstance.current.setOption(option);

    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, [sensorData, isLoading, bounds, displayName, unit]);

  return chartRef;
};
